import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./VerificationCode.css";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Snackbar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { axiosCall } from "../utils/axiosCall";
import constants from "../config/constants";
import { setResetToken, userLogin } from "../redux/features/userSlice";

const VerificationCode = () => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [seconds, setSeconds] = useState(120);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    let interval = null;
    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    } else if (seconds === 0) {
      setIsActive(false);
      setSeconds(120); 
    }
    return () => clearInterval(interval);
  }, [isActive, seconds]);

  const [errorMessage, setErrorMessage] = useState("");

  const handleCodeChange = (index, value) => {
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    } else if (!value && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.join("")) {
      setErrorMessage("Please fill otp.");
      return;
    }

    try {
      const isLoginVerification = user.verificationType === "LOGIN";
      const verifyEndpoint = isLoginVerification
        ? constants.OTP_VERIFY
        : constants.VERIFY_RESET_OTP;

      const otpVerfication = await axiosCall(
        constants.POST_METHOD,
        verifyEndpoint,
        { emailOrPhone: user.email, otp: code.join("") },
        null,
        null
      );

      if (otpVerfication.isSuccess) {
        if (isLoginVerification) {
          dispatch(userLogin(otpVerfication?.data?.data));
          navigate("/patient");
        } else {
          const resetToken = otpVerfication?.data?.data?.resetToken;
          if (!resetToken) {
            setErrorMessage("Reset token not received. Please try again.");
            return;
          }
          dispatch(setResetToken(resetToken));
          navigate("/reset-password");
        }
      } else {
        setErrorMessage(otpVerfication.error.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleResendCode = async (e) => {
    e.preventDefault();
    console.log("Resending code...");
    // Add logic to resend the verification code
    try {
      const resendOTP = await axiosCall(
        constants.POST_METHOD,
        constants.RESEND_CODE,
        { emailOrPhone: user.email, type: user.verificationType },
        null,
        null
      );

      if (resendOTP.isSuccess) {
        setCode(["", "", "", "", "", ""]);
        setOpen(true);
        setIsActive(true)
      } else {
        setErrorMessage(resendOTP.error.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  return (
    <Box display="flex" height="100vh">
      {/* Left Image Section */}
      <Box width="50%" overflow="hidden">
        <img
          src="/Images/leftlogo.png"
          alt="Doctor holding scalpel"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {/* Right Content Section */}
      <Box
        maxWidth={"50%"}
        p={5}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        ml={5}
      >
        {/* Logo and Text */}
        <Box display="flex" alignItems="center" mb={3} mt={3}>
          <img
            src="/Images/plus.png"
            alt="Plus sign"
            style={{ width: 40, height: 40, marginRight: 12, marginTop: -20 }}
          />
          <Box ml={2} mb={-3} fontSize={12} lineHeight={1.2}>
            <Typography
              variant="h6"
              color="#00C6F2"
              fontWeight="bold"
              style={{ marginTop: -40 }}
            >
              HOSPITAL MANAGEMENT
            </Typography>
            <Typography variant="h6" color="textSecondary" fontWeight={600}>
              INFORMATION SYSTEM
            </Typography>
          </Box>
        </Box>

        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          mt={-7}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Welcome to
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color="textPrimary"
            gutterBottom
          >
            HMIS PORTAL
          </Typography>
          <Typography
            variant="h6"
            color="#00C6F2"
            fontWeight="bold"
            mb={2}
            fontFamily="Helvetica"
            gutterBottom
          >
            HOSPITAL LOGO
          </Typography>

          <p className="enter">Enter Your Code</p>
          <p className="email-sent">
            We sent a code to <span className="email">{user?.email}</span>
          </p>
          <form onSubmit={handleSubmit}>
            <div className="code-input">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  required
                />
              ))}
            </div>
            {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
            <button type="submit" className="next-button">
              {user.verificationType === "LOGIN" ? "LOGIN" : "VERIFY"}
            </button>
          </form>
          <p className="resend-code">
            Didn't receive the code?{" "}
            {isActive ? (
              `Please wait ${seconds} seconds`
            ) : (
              <a href="#" onClick={handleResendCode}>
                Click to resend
              </a>
            )}
          </p>
        </Box>
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        message="OTP sent successfully!"
      />
    </Box>
  );
};

export default VerificationCode;
